/**
 * Operator-facing place for an unscheduled stop.
 *
 * "outside any hub" names a category, not a location. The card banner has room for one
 * short line, and the line an operator can act on is the hub — the one the truck is in,
 * or the nearest one it is sitting next to.
 */
import { haversineKm } from './corridor'
import { pointInRing } from './pointInPolygon'
import type { GeofenceFeature, GeoPayload } from './types'

/** Same destination types as corridorPlaces / GeofenceDef.isHub(). */
export const HUB_TYPES = new Set(['plant', 'depot', 'yard', 'hub'])

/** Within this, the stop is "near" the hub rather than a distance. */
export const NEAR_HUB_KM = 5

/** Fences closer than this are the same place (DHAR plant + DHAR-YARD). */
const SAME_PLACE_KM = 4

const ANY_HUB_RE = /stopped\s+(\d+)\s+min\s+outside any hub/i

export interface HubPlace {
  /** Short operator code: DHAR, not dhar-plant. */
  name: string
  lat: number
  lon: number
}

export interface StopPlace {
  relation: 'at' | 'near' | 'from'
  name: string
  km: number
}

type GeoFences = GeoPayload['geofences'] | null | undefined

function hubLabel(hubCode: string | null | undefined, name: string | null | undefined): string | null {
  const code = hubCode?.trim()
  if (code) return code
  const n = name?.trim()
  return n || null
}

function hubFeatures(geofences: GeoFences): GeofenceFeature[] {
  return (geofences?.features ?? []).filter((f) =>
    HUB_TYPES.has(String(f.properties.type).toLowerCase()))
}

/**
 * Destinations only, collapsed by proximity. The shortest hub_code in a cluster is the
 * parent — DHULE, not DHULE-YARD-IN.
 */
export function hubPlaces(geofences: GeoFences): HubPlace[] {
  const stops: HubPlace[] = []
  for (const f of hubFeatures(geofences)) {
    const p = f.properties
    const name = hubLabel(p.hub_code, p.name)
    if (!name || p.centroid_lat == null || p.centroid_lon == null) continue
    stops.push({ name, lat: p.centroid_lat, lon: p.centroid_lon })
  }
  const places: HubPlace[] = []
  for (const s of stops) {
    const near = places.find((p) => haversineKm(p.lat, p.lon, s.lat, s.lon) <= SAME_PLACE_KM)
    if (!near) places.push({ ...s })
    else if (s.name.length < near.name.length) near.name = s.name
  }
  return places
}

function parentName(lat: number, lon: number, places: HubPlace[]): string | null {
  let best: HubPlace | null = null
  let bestKm = Infinity
  for (const p of places) {
    const km = haversineKm(lat, lon, p.lat, p.lon)
    if (km < bestKm) { best = p; bestKm = km }
  }
  return best?.name ?? null
}

export function resolveStopPlace(
  lat: number | null | undefined,
  lon: number | null | undefined,
  geofences: GeoFences,
): StopPlace | null {
  if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) return null
  const places = hubPlaces(geofences)
  if (!places.length) return null

  for (const f of hubFeatures(geofences)) {
    const ring = f.geometry?.coordinates?.[0]
    if (!ring || ring.length < 4) continue
    if (!pointInRing(lon, lat, ring)) continue
    const p = f.properties
    const name = parentName(p.centroid_lat, p.centroid_lon, places)
      ?? hubLabel(p.hub_code, p.name)
    if (!name) continue
    return { relation: 'at', name, km: 0 }
  }

  let best: HubPlace | null = null
  let bestKm = Infinity
  for (const p of places) {
    const km = haversineKm(lat, lon, p.lat, p.lon)
    if (km < bestKm) { best = p; bestKm = km }
  }
  if (!best) return null
  return {
    relation: bestKm <= NEAR_HUB_KM ? 'near' : 'from',
    name: best.name,
    km: bestKm,
  }
}

export function formatUnscheduledStop(minutes: number, place: StopPlace | null): string {
  const n = Math.max(0, Math.round(minutes))
  if (!place) return `stopped ${n} min`
  if (place.relation === 'at') return `stopped ${n} min at ${place.name}`
  if (place.relation === 'near') return `stopped ${n} min near ${place.name}`
  const d = place.km >= 10 ? place.km.toFixed(0) : place.km.toFixed(1)
  return `stopped ${n} min · ${d} km from ${place.name}`
}

/**
 * Rewrite stored "outside any hub" copy using live geometry. Other messages pass through.
 * Historical alerts keep the old sentence in Kafka/Postgres; this is the operator view.
 */
export function rewriteStopMessage(
  message: string | null | undefined,
  lat: number | null | undefined,
  lon: number | null | undefined,
  geofences: GeoFences,
): string {
  const raw = message ?? ''
  const m = raw.match(ANY_HUB_RE)
  if (!m) return raw
  return formatUnscheduledStop(Number(m[1]), resolveStopPlace(lat, lon, geofences))
}
